import React, { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * <Term> — Phase 1.5b glossary popover (+Phase 2f icon trigger)
 *
 * Two trigger styles:
 *   <Term term="erisaExemption">ERISA exemption</Term>   // inline dotted underline
 *   <Term term="erisaExemption" icon />                  // standalone "?" icon
 *
 * Keyboard: Enter/Space opens, Escape closes. When open, focus stays on the
 * trigger (a11y bubble); clicking outside closes. Mobile: tap-to-open,
 * tap-anywhere-else to dismiss.
 *
 * Content comes from `i18n/terms.{en,es}.json` via the `terms` namespace:
 *   { title: "...", body: "..." }
 */
export function Term({ term, children, icon = false, className = "" }) {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const popId = useId();
    const triggerRef = useRef(null);

    // Lazy-loaded (terms are bundled with the i18n resources).
    const title = t(`${term}.title`, { ns: "terms", defaultValue: term });
    const body = t(`${term}.body`, { ns: "terms", defaultValue: "" });

    // Outside click + escape dismiss.
    useEffect(() => {
        if (!open) return undefined;
        function onKey(e) {
            if (e.key === "Escape") {
                setOpen(false);
                triggerRef.current?.focus();
            }
        }
        function onClick(e) {
            if (!triggerRef.current) return;
            if (!triggerRef.current.contains(e.target)) setOpen(false);
        }
        window.addEventListener("keydown", onKey);
        window.addEventListener("mousedown", onClick, true);
        return () => {
            window.removeEventListener("keydown", onKey);
            window.removeEventListener("mousedown", onClick, true);
        };
    }, [open]);

    const toggle = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen((v) => !v);
    };
    const onKeyDown = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
        }
    };

    const triggerCommon = {
        ref: triggerRef,
        type: "button",
        onClick: toggle,
        onKeyDown,
        "aria-describedby": open ? popId : undefined,
        "aria-expanded": open,
        "data-testid": `term-trigger-${term}`,
        lang: i18n.language || "en",
    };

    return (
        <span className={`term-wrap ${className}`.trim()}>
            {icon ? (
                <button
                    {...triggerCommon}
                    className={`term-icon ${open ? "is-open" : ""}`}
                    aria-label={title ? `Help: ${title}` : "Help"}
                >
                    <span aria-hidden="true">?</span>
                </button>
            ) : (
                <button
                    {...triggerCommon}
                    className={`term-trigger ${open ? "is-open" : ""}`}
                >
                    {children}
                </button>
            )}
            {open && (
                <span
                    className="term-pop"
                    id={popId}
                    role="tooltip"
                    data-testid={`term-pop-${term}`}
                >
                    {title && <span className="term-pop-title">{title}</span>}
                    <span
                        className="term-pop-body"
                        // Body can contain <strong> — rendered as HTML to preserve those tags.
                        dangerouslySetInnerHTML={{ __html: body }}
                    />
                </span>
            )}
        </span>
    );
}

export default Term;
